import {
  Checkbox,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { Fragment, useEffect } from "react";
import { DataTable } from "~/components/data-table";
import { usePolicies } from "./hooks/usePolicies";
import { ActivePolicyEdge, SelectedPolicies } from "./types";
import { OrderDirection } from "~/operations";
import { pluralize } from "~/lib/pluralize";
import { BlockOutlined, NotificationsOffOutlined } from "@mui/icons-material";
import { Pagination, PaginationProps } from "~/components/pagination";
import { isEmpty } from "lodash-es";
import { EmptyState } from "~/components/empty-state/empty-state";
import { PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { PolicyListRow } from "~/pages/security/policies/components/PolicyListRow";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { TriageToolbarButton } from "~/components/TriageToolbar/TriageToolbarButton";
import { BarchartIcon } from "~/icons";

type ListProps = {
  scope: SpaceOrWorkspaceScope;
  selectedItems: SelectedPolicies;
  handleCheckAll: (edges: ActivePolicyEdge[]) => void;
  activePolicies: ActivePolicyEdge[];
  totalPolicies: number;
  permissions: ReturnType<typeof usePolicies>["permissions"];
  handle: ReturnType<typeof usePolicies>["handle"];
  state: ReturnType<typeof usePolicies>["state"];
  orderBy: ReturnType<typeof usePolicies>["orderBy"];
  PaginationProps: PaginationProps;
};

type Header = {
  id: string;
  label: string;
  sortable?: boolean;
  options?: {
    minWidth?: number;
    align?: "inherit" | "left" | "center" | "right" | "justify";
    colSpan?: number;
    pl?: number;
  };
};
const tableHeaders: Header[] = [
  {
    id: "NAME",
    label: "Policy",
    sortable: true,
    options: { colSpan: 2, pl: 1.5 },
  },
  {
    id: "RISK",
    label: "Risk",
    sortable: false,
  },
  {
    id: "AFFECTED_ASSETS",
    sortable: true,
    label: "Asset results",
    options: { minWidth: 300 },
  },
];

export function List({
  scope,
  permissions,
  selectedItems,
  handle,
  state,
  orderBy,
  handleCheckAll,
  activePolicies,
  totalPolicies,
  PaginationProps,
}: ListProps) {
  const triageToolbar = useTriageToolbar();

  useEffect(() => {
    if (selectedItems.length > 0) {
      triageToolbar.show({
        content: (
          <>
            {(state.hasSelectedBothEnabledAndPreviewedPolicies ||
              state.hasSelectedPreviewedPolicy) && (
              <TriageToolbarButton
                label={`Enable ${pluralize("Policy", selectedItems.length)}`}
                onClick={handle.enablePolicies}
                data-testid="toolbar-policies-enable"
                key="POLICIES_ENABLE"
                icon={<BarchartIcon />}
              />
            )}
            <TriageToolbarButton
              label={`Remove ${pluralize("Policy", selectedItems.length)}`}
              onClick={handle.disablePolicies}
              data-testid="toolbar-policies-remove"
              key="POLICIES_REMOVE"
              icon={<BlockOutlined />}
            />
            {(state.hasSelectedBothEnabledAndPreviewedPolicies ||
              state.hasSelectedEnabledPolicy) && (
              <TriageToolbarButton
                label={`Preview ${pluralize("Policy", selectedItems.length)}`}
                onClick={handle.previewPolicies}
                data-testid="toolbar-policies-enable"
                key="POLICIES_ENABLE"
                icon={<NotificationsOffOutlined />}
              />
            )}
          </>
        ),
        payload: {
          count: selectedItems.length,
          totalCount: totalPolicies,
          onClose: handle.unCheckAll,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedItems]);

  if (isEmpty(activePolicies)) {
    return <EmptyState contentType="policies" />;
  }

  return (
    <Fragment>
      <DataTable
        selectable={permissions.edit}
        selection={selectedItems}
        id="enabled-policies-list"
      >
        <TableHead>
          <TableRow>
            {permissions.edit && (
              <TableCell>
                <Checkbox
                  checked={selectedItems.length === activePolicies?.length}
                  indeterminate={
                    selectedItems.length > 0 &&
                    selectedItems.length < activePolicies.length
                  }
                  onChange={() => handleCheckAll(activePolicies)}
                />
              </TableCell>
            )}
            {tableHeaders.map((header: Header) => (
              <TableCell
                key={header.id}
                align={header.options?.align}
                colSpan={header.options?.colSpan}
                sx={{ minWidth: header.options?.minWidth }}
              >
                {header.sortable ? (
                  <TableSortLabel
                    onClick={() => handle.sortClick(header.id)}
                    direction={
                      orderBy.direction === OrderDirection.Asc ? "asc" : "desc"
                    }
                    active={orderBy.field === header.id}
                    sx={{ pl: header.options?.pl }}
                  >
                    {header.label}
                  </TableSortLabel>
                ) : (
                  header.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {/* Enabled Policies List */}
        <TableBody>
          {activePolicies.map((edge) => {
            if (!edge.node) return undefined;
            const { action, mrn } = edge.node;

            const href = `/space/findings/policies/${PolicyMrnToURIEncodedId(
              mrn,
            )}?${scope.params}`;

            return (
              <PolicyListRow
                key={mrn}
                onClick={() => handle.navigate(href)}
                policy={edge.node}
                canEdit={permissions.edit}
                onCheckboxSelect={(e) =>
                  handle.check(e, {
                    policyMrn: mrn,
                    action,
                  })
                }
                isSelected={Boolean(
                  selectedItems.find((i) => i.policyMrn === mrn),
                )}
              />
            );
          })}
        </TableBody>
      </DataTable>
      {PaginationProps.pageInfo && <Pagination {...PaginationProps} />}
    </Fragment>
  );
}
